import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";
import { EventModel, EventType } from "app/models/EventModel";
import { followingEditPayload } from "app/routes/Calendar/EventForm/seriesBay";
import { notifyStudentsFrom } from "./notifyStudents";
import {
  deleteEvent,
  initializeNewEvent,
  createEvent,
  editEvent,
  startEdit,
  deselectEvent,
  initializeCopyEvent,
} from "app/actions/events";

interface UseEventHandlersParams {
  calendarRef: React.RefObject<any>;
  selectedEvent: EventModel;
  calendarView: string;
  currentDay: any;
  timeZone: string;
  lessonRates: any[];
  requestConfirmation: (payload: any) => void;
  refundRequestConfirmation: (payload: any) => void;
}

const useEventHandlers = ({
  calendarRef,
  selectedEvent,
  calendarView,
  currentDay,
  timeZone,
  lessonRates,
  requestConfirmation,
  refundRequestConfirmation,
}: UseEventHandlersParams) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const onDeleteEvent = () => {
    const isNonRefundable = selectedEvent.refundable === false;
    const isOnline = selectedEvent.payment_method === "online";
    const isPackage = selectedEvent.payment_method === "package";

    const content =
      selectedEvent.type === "coach_schedule_block"
        ? t("Please confirm that you would like to cancel this event.")
        : t("Please confirm that you would like to delete this session.");

    const title =
      selectedEvent.type === "coach_schedule_block"
        ? t("Delete Unavailability")
        : t("Cancel Session");

    // Don't show refund modal for non-refundable sessions
    const showRefundModal = !isNonRefundable && (isOnline || isPackage);
    const confirmationFn = showRefundModal
      ? refundRequestConfirmation
      : requestConfirmation;

    confirmationFn({
      event: selectedEvent,
      content,
      title,
      operation: "delete",
      actions: {
        onConfirm: (_data, options) => {
          return dispatch(deleteEvent(selectedEvent, undefined, false, notifyStudentsFrom(options)));
        },
        onConfirmFollowing: (_data, options) => {
          return dispatch(deleteEvent(selectedEvent, "following", false, notifyStudentsFrom(options)));
        },
        onChargeLateCancellation: (_data, options) => {
          return dispatch(deleteEvent(selectedEvent, undefined, true, notifyStudentsFrom(options)));
        },
      },
    });
  };

  const onSaveEventForm = (data) => {
    if (data.id) {
      const payload = EventModel.apiPayload(data, "update");
      let content = t(
        "Please confirm that you would like to edit this session.",
      );
      if (data.recurrenceGroupId) {
        content = t(
          "Your session is set to \u201CRepeating - {{repeat}}\u201D. You can choose whether you wish to apply these changes to the repeating dates as well.",
          { repeat: capitalize(selectedEvent.repeat) },
        );
      }
      requestConfirmation({
        event: selectedEvent,
        content,
        title: t("Edit Event"),
        operation: "edit",
        actions: {
          onConfirm: () => {
            dispatch(editEvent(payload));
          },
          // A series edit sends the bay only when the user picked one or the
          // bay changed. See bayKeyForScope.
          onConfirmFollowing: () => {
            dispatch(
              editEvent(
                followingEditPayload(payload, data, selectedEvent?.scheduleResourceId),
              ),
            );
          },
        },
      });
    } else {
      const payload = EventModel.apiPayload(data, "create");
      dispatch(createEvent(payload));
    }
  };

  const onEditEvent = () => {
    dispatch(startEdit());
  };

  const onCopyEvent = () => {
    dispatch(initializeCopyEvent(selectedEvent));
  };

  const onEditProgram = () => {
    window.location.replace(`/programs/${selectedEvent?.id}/view`);
  };

  const onCancelSelection = () => {
    calendarRef.current.getApi().unselect();
    dispatch(deselectEvent());
  };

  const initNewEvent = () => {
    const newEventDate =
      (calendarView === "timeGridNarrow" ||
        calendarView === "timeGridDay" ||
        calendarView === "listWeek") &&
      currentDay
        ? currentDay
        : null;

    const defaultBookingOption = lessonRates?.find(
      (rate) => rate.defaultRate === true,
    );
    const rate = defaultBookingOption?.rate;
    const location = defaultBookingOption?.location;

    dispatch(
      initializeNewEvent({
        start: null,
        end: null,
        date: newEventDate || null,
        type: EventType.LESSON_SCHEDULE,
        timeZone,
        backgroundColor: "#0279b3",
        lessonRateId: defaultBookingOption?.id,
        rate,
        location,
      }),
    );
  };

  return {
    onDeleteEvent,
    onSaveEventForm,
    onEditEvent,
    onCopyEvent,
    onEditProgram,
    onCancelSelection,
    initNewEvent,
  };
};

export default useEventHandlers;
